/**
 * ScreeningPanel — Feb 2026 v10 · Admin Superset design
 *
 *   • Neutral slate/grey palette. Accent colours ONLY as inline icons:
 *       green ✓ for included · red · for excluded.
 *   • Each category card shows the first 8 rules inline by default.
 *     If there are more, a single "Show all N" toggle reveals the rest
 *     in place — no more "+6 more" gating that the user objected to.
 *   • Category cards render as a clean 3-column grid to reduce noise.
 *
 * Backend contract unchanged — reads the same
 * /api/viatris/screening-definition response.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Loader2, Pencil, Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import { ScreeningEditor } from './ScreeningEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PLATFORM_HINTS = /mrna|dna\s*vaccine|vaccine|sirna|antisense|aso|platform|technology/i;
const DEFAULT_VISIBLE = 8;   // items to render before "Show all" toggle

function CategoryCard({ id, title, kind, items }) {
  const [showAll, setShowAll] = useState(false);
  const count = items.length;
  if (count === 0) return null;

  const visible = showAll || count <= DEFAULT_VISIBLE + 2
    ? items
    : items.slice(0, DEFAULT_VISIBLE);
  const hidden = count - visible.length;
  const isInclude = kind === 'include';
  const dotCls = isInclude ? 'bg-emerald-500' : 'bg-rose-500';
  const chipCls = isInclude
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : 'bg-rose-50 border-rose-200 text-rose-800';

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col gap-2"
      data-testid={`screening-card-${id}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-700 inline-flex items-center gap-1.5">
          <span className={`inline-block w-2 h-2 rounded-full ${dotCls}`} />
          {title}
        </div>
        <span className="text-[10px] text-slate-500 tabular-nums">
          {count} {count === 1 ? 'rule' : 'rules'}
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        {visible.map((it, i) => (
          <span
            key={i}
            className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium leading-tight ${chipCls}`}
            data-testid={`screening-rule-${id}-${it.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            title={it}
          >
            {it}
          </span>
        ))}
      </div>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          className="text-[10px] text-slate-500 hover:text-slate-800 font-medium self-start inline-flex items-center gap-0.5"
          data-testid={`screening-card-toggle-${id}`}
        >
          {showAll ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {showAll ? 'Show less' : `Show all ${count}`}
        </button>
      )}
    </div>
  );
}

export function ScreeningPanel({ engagementId = 'eng-viatris-china', cohort: _cohort = '', compact: _compact = false, editable = true, clientToken = '' }) {
  const [def, setDef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  // Feb 2026 v11 · Screening rules default to COLLAPSED so they don't
  // consume screen real estate. A compact summary strip stays visible
  // (label + rule counts + Edit). Click the header to expand into the
  // 5-card grid.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = clientToken
          ? `${API}/client/viatris/${clientToken}/screening-definition`
          : `${API}/viatris/screening-definition?engagement_id=${engagementId}`;
        const r = await axios.get(url, { withCredentials: true });
        if (!cancelled) setDef(r.data);
      } catch (e) {
        console.warn('[ScreeningPanel] load failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [engagementId, clientToken, reloadTick]);

  const cards = useMemo(() => {
    if (!def) return null;
    // Inclusion rules — expand stages_regex into readable strings.
    const inclusions = [];
    const stagesRegex = def?.include?.stages_regex || '';
    if (stagesRegex) {
      if (/phase\s*3|phase\s*iii/i.test(stagesRegex)) inclusions.push('Phase 3+');
      if (/nda|bla|filed/i.test(stagesRegex)) inclusions.push('Filed / NDA / BLA');
      if (/approved|registered/i.test(stagesRegex)) inclusions.push('Approved / Registered');
      if (/marketed|launched|commercial/i.test(stagesRegex)) inclusions.push('Marketed / Commercial');
      if (inclusions.length === 0) inclusions.push('Custom stage rule');
    }
    const taInclRegex = def?.include?.therapeutic_areas_regex || '';
    if (taInclRegex && !/^(\(\|\)|\)$)/.test(taInclRegex)) {
      const inside = taInclRegex.replace(/^[(^]?\(|\)[)$]?$/g, '');
      inclusions.push(...inside.split('|').filter(Boolean)
        .map(t => `TA · ${t.charAt(0).toUpperCase() + t.slice(1)}`));
    }

    const modalitiesRaw = def?.exclude?.modalities || [];
    const platforms = modalitiesRaw.filter(m => PLATFORM_HINTS.test(m));
    const modalities = modalitiesRaw.filter(m => !PLATFORM_HINTS.test(m));

    const business = [
      ...(def?.exclude?.asset_types || []).map(t => `Asset type · ${t}`),
      ...(def?.exclude?.licensing_status || []).map(t => `Licensing · ${t}`),
      ...(def?.exclude?.keywords || []).map(t => `Keyword · "${t}"`),
    ];

    return {
      inclusions,
      tas: def?.exclude?.therapeutic_areas || [],
      modalities,
      platforms,
      business,
    };
  }, [def]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-slate-500 px-3 py-2 rounded-lg border border-slate-200 bg-white">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading screening criteria…
      </div>
    );
  }
  if (!def || !cards) return null;

  const totalRuleCount =
    (cards.inclusions?.length || 0) +
    (cards.tas?.length || 0) +
    (cards.modalities?.length || 0) +
    (cards.platforms?.length || 0) +
    (cards.business?.length || 0);

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="screening-panel"
    >
      {/* Header — always visible summary strip */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full px-3 py-2 flex items-center justify-between gap-3 hover:bg-slate-50 rounded-lg text-left"
        data-testid="screening-panel-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="w-4 h-4 text-slate-600 shrink-0" />
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Screening rules</span>
          <span className="text-[12px] font-semibold text-slate-800 truncate" data-testid="screening-version">
            {def?.version || 'Default'}
          </span>
          <span className="text-[10px] text-slate-500 hidden md:inline">
            · {totalRuleCount} rules · {cards.inclusions.length} incl · {(cards.tas.length + cards.modalities.length + cards.platforms.length + cards.business.length)} excl
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {editable && (
            <span
              onClick={(e) => { e.stopPropagation(); setEditorOpen(true); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setEditorOpen(true); } }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-200 bg-white hover:bg-slate-100 text-slate-600 text-[11px] font-medium cursor-pointer"
              data-testid="screening-edit-btn"
            >
              <Pencil className="w-3 h-3" /> Edit
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {editable && (
        <ScreeningEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          engagementId={engagementId}
          onSaved={() => setReloadTick(t => t + 1)}
        />
      )}

      {expanded && (
        <div className="px-3 pb-3 border-t border-slate-100 pt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            <CategoryCard id="include"              title="Included"                          kind="include" items={cards.inclusions} />
            <CategoryCard id="ta-exclusions"        title="Excluded Therapeutic Areas"        kind="exclude" items={cards.tas} />
            <CategoryCard id="modality-exclusions"  title="Excluded Modalities"               kind="exclude" items={cards.modalities} />
            <CategoryCard id="platform-exclusions"  title="Excluded Technology / Platforms"   kind="exclude" items={cards.platforms} />
            <CategoryCard id="business-exclusions"  title="Business Rules"                    kind="exclude" items={cards.business} />
          </div>
        </div>
      )}
    </div>
  );
}

export default ScreeningPanel;
